import { t } from "../lib/i18n";
import React, { useState, useMemo } from 'react';
import Tooltip from './Tooltip';

export const UnderwritingToolkit: React.FC = () => {
    const [inputs, setInputs] = useState(() => {
        const defaults = {
            purchasePrice: 1000000,
            grossRentalIncome: 100000,
            otherIncome: 0,
            vacancyRate: 5,
            operatingExpenses: 40000,
            capex: 5000,
            loanAmount: 750000,
            interestRate: 5.0,
            loanTerm: 30,
            closingCosts: 3,
        };
        try {
            const saved = localStorage.getItem('pending_screener_deal');
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    purchasePrice: parsed.purchasePrice || defaults.purchasePrice,
                    grossRentalIncome: parsed.grossRentalIncome || defaults.grossRentalIncome,
                    otherIncome: 0,
                    vacancyRate: parsed.vacancyRate ?? defaults.vacancyRate,
                    operatingExpenses: parsed.operatingExpenses ?? defaults.operatingExpenses,
                    capex: parsed.capex ?? defaults.capex,
                    loanAmount: parsed.results?.loanAmount || (parsed.purchasePrice * ((parsed.ltvPercent || 65) / 100)) || defaults.loanAmount,
                    interestRate: parsed.interestRate || defaults.interestRate,
                    loanTerm: parsed.loanTerm || defaults.loanTerm,
                    closingCosts: 3,
                };
            }
        } catch (e) {
            // ignore
        }
        return defaults;
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInputs(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    };

    const results = useMemo(() => {
        const { purchasePrice, grossRentalIncome, otherIncome, vacancyRate, operatingExpenses, capex, loanAmount, interestRate, loanTerm, closingCosts } = inputs;

        const effectiveGrossIncome = grossRentalIncome * (1 - vacancyRate / 100) + otherIncome;
        const noi = effectiveGrossIncome - operatingExpenses - capex;
        const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0;

        const downPayment = purchasePrice - loanAmount;
        const totalClosingCosts = purchasePrice * (closingCosts / 100);
        const totalCashInvested = downPayment + totalClosingCosts;

        const monthlyInterestRate = (interestRate / 100) / 12;
        const numberOfPayments = loanTerm * 12;
        const annualDebtService = numberOfPayments > 0 
            ? (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1) * 12
            : 0;

        const cashFlow = noi - annualDebtService;
        const cashOnCashReturn = totalCashInvested > 0 ? (cashFlow / totalCashInvested) * 100 : 0;

        const dscr = annualDebtService > 0 ? noi / annualDebtService : Infinity;
        const grm = grossRentalIncome > 0 ? purchasePrice / grossRentalIncome : 0;
        const ltv = purchasePrice > 0 ? (loanAmount / purchasePrice) * 100 : 0;

        return {
            noi,
            capRate,
            annualDebtService,
            cashFlow,
            cashOnCashReturn,
            downPayment,
            totalCashInvested,
            dscr,
            grm,
            ltv,
            effectiveGrossIncome,
            totalClosingCosts
        };
    }, [inputs]);

    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <div className="flex items-center mb-6">
                <h1 className="text-3xl font-bold text-text-secondary">{t("underwritingToolkit.k1")}</h1>
                <Tooltip text="This page provides a powerful toolkit for underwriting potential deals. Enter the key financial details of a property to calculate important metrics like Net Operating Income (NOI), Cap Rate, and Cash-on-Cash Return. This will help you quickly assess the financial viability of a deal." />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Input Form */}
                <div className="bg-bg-secondary p-6 rounded-lg shadow border border-border-primary">
                    <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("underwritingToolkit.k2")}</h2>
                    <div className="space-y-4">
                        {/* Purchase Price */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k3")}</label>
                            <input type="number" name="purchasePrice" value={inputs.purchasePrice} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Gross Annual Rental Income */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k4")}</label>
                            <input type="number" name="grossRentalIncome" value={inputs.grossRentalIncome} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Other Income */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k5")}</label>
                            <input type="number" name="otherIncome" value={inputs.otherIncome} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Vacancy Rate */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k6")}</label>
                            <input type="number" step="0.1" name="vacancyRate" value={inputs.vacancyRate} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Annual Operating Expenses */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k7")}</label>
                            <input type="number" name="operatingExpenses" value={inputs.operatingExpenses} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Annual CapEx */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k8")}</label>
                            <input type="number" name="capex" value={inputs.capex} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Loan Amount */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k9")}</label>
                            <input type="number" name="loanAmount" value={inputs.loanAmount} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Interest Rate */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k10")}</label>
                            <input type="number" step="0.01" name="interestRate" value={inputs.interestRate} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Loan Term */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k11")}</label>
                            <input type="number" name="loanTerm" value={inputs.loanTerm} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                        {/* Closing Costs */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary">{t("underwritingToolkit.k12")}</label>
                            <input type="number" step="0.1" name="closingCosts" value={inputs.closingCosts} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-bg-primary border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary" />
                        </div>
                    </div>
                </div>

                {/* Results Display */}
                <div className="bg-bg-secondary p-6 rounded-lg shadow border border-border-primary">
                    <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("underwritingToolkit.k13")}</h2>
                    <div className="space-y-4 text-lg">
                        {/* Effective Gross Income */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k14")}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(results.effectiveGrossIncome)}</span>
                        </div>
                        {/* NOI */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k15")}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(results.noi)}</span>
                        </div>
                        {/* Cap Rate */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k16")}</span>
                            <span className="font-bold text-green-500">{results.capRate.toFixed(2)}%</span>
                        </div>
                        {/* Total Cash Invested */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k17")}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(results.totalCashInvested)}</span>
                        </div>
                        {/* Annual Debt Service */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k18")}</span>
                            <span className="font-bold text-red-500">{formatCurrency(results.annualDebtService)}</span>
                        </div>
                        {/* Annual Cash Flow */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k19")}</span>
                            <span className={`font-bold ${results.cashFlow >= 0 ? 'text-green-500' : 'text-red-500'}`}>{formatCurrency(results.cashFlow)}</span>
                        </div>
                        {/* Cash-on-Cash Return */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k20")}</span>
                            <span className={`font-bold ${results.cashOnCashReturn >= 0 ? 'text-green-500' : 'text-red-500'}`}>{results.cashOnCashReturn.toFixed(2)}%</span>
                        </div>
                        {/* DSCR */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k21")}</span>
                            <span className={`font-bold ${results.dscr >= 1.25 ? 'text-green-500' : 'text-red-500'}`}>{results.dscr.toFixed(2)}</span>
                        </div>
                        {/* GRM */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k22")}</span>
                            <span className="font-bold text-text-primary">{results.grm.toFixed(2)}x</span>
                        </div>
                        {/* LTV */}
                        <div className="flex justify-between items-center p-3 bg-bg-primary rounded-md">
                            <span className="font-semibold text-text-secondary">{t("underwritingToolkit.k23")}</span>
                            <span className="font-bold text-text-primary">{results.ltv.toFixed(2)}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
